import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle, Clock, FileText, Package, Euro, Calendar, AlertTriangle, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';

const API = window.location.origin + '/api';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_a85f478b-763e-4b7e-9f54-0dbb5a37a168/artifacts/nenk0vgh_Pack%20%C3%A9cran%20LED%207m2.png";
const LOGO_DARK = "https://customer-assets.emergentagent.com/job_7f1baddf-ad9a-4bf0-8f10-77cebc2c701b/artifacts/wao4t6w8_Logo%20%28N%29.png";

const formatDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
  } catch { return iso; }
};

export default function QuoteAcceptPage() {
  const { quoteId } = useParams();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';

  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(false);
  const [error, setError] = useState('');
  const [redirectLink, setRedirectLink] = useState(null);
  const [accepted, setAccepted] = useState(false);

  useEffect(() => {
    const fetchQuote = async () => {
      try {
        const res = await axios.get(`${API}/quote-accept/${quoteId}?token=${token}`);
        setQuote(res.data);
        if (res.data.status === 'accepted' || res.data.status === 'converted') {
          setAccepted(true);
          // If a reservation link exists (manual_order created but not yet paid), expose it
          // so the client can click through to the payment flow.
          if (res.data.reservation_link) {
            setRedirectLink(res.data.reservation_link);
          }
        }
      } catch (err) {
        setError(err.response?.data?.detail || 'Devis introuvable ou lien invalide');
      } finally {
        setLoading(false);
      }
    };
    fetchQuote();
  }, [quoteId, token]);

  const handleAccept = async () => {
    setAccepting(true);
    try {
      const res = await axios.post(`${API}/quote-accept/${quoteId}`, { token });
      const status = res.data.status;
      const link = res.data.reservation_link;

      // Auto-redirect to /reservation so the client picks CB or SEPA in the same flow.
      if (link && (status === 'accepted_and_converted' || status === 'already_converted')) {
        // Tiny delay so the success state flashes before the redirect (better UX feedback)
        setAccepted(true);
        setQuote(prev => ({ ...prev, status: 'accepted' }));
        setRedirectLink(link);
        setTimeout(() => { window.location.href = link; }, 1500);
        return;
      }
      if (status === 'accepted' || status === 'already_accepted') {
        setAccepted(true);
        setQuote(prev => ({ ...prev, status: 'accepted' }));
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur lors de l\'acceptation');
    } finally {
      setAccepting(false);
    }
  };

  // Loading
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  // Error
  if (error && !quote) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md text-center" data-testid="quote-accept-error">
          <AlertTriangle className="w-12 h-12 text-amber-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-slate-900 mb-2">Lien invalide</h2>
          <p className="text-slate-500">{error}</p>
        </div>
      </div>
    );
  }

  // Success state
  if (accepted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-lg p-10 max-w-lg text-center" data-testid="quote-accept-success">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-green-600" />
          </div>
          <h2 className="text-2xl font-bold text-slate-900 mb-3">Devis accepté !</h2>
          <p className="text-slate-500 mb-4">
            Votre devis <strong className="text-slate-700">{quote.quote_number}</strong> a bien été accepté.
          </p>

          <div className="bg-green-50 border border-green-200 rounded-xl p-5 mb-6 text-left">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center shrink-0 mt-0.5">
                <Clock className="w-5 h-5 text-green-600" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-green-800 mb-1">
                  {redirectLink ? 'Redirection vers le paiement…' : 'Et maintenant ?'}
                </h3>
                <p className="text-green-700 text-sm leading-relaxed">
                  {redirectLink ? (
                    <>
                      Vous allez être redirigé vers la page de paiement pour finaliser votre commande
                      (carte bancaire ou prélèvement SEPA). Si rien ne se passe dans 5 secondes,
                      <a href={redirectLink} className="font-bold underline ml-1">cliquez ici</a>.
                    </>
                  ) : (
                    <>
                      Votre devis a été accepté. Continuez vers le paiement pour choisir votre mode (carte bancaire ou prélèvement SEPA).
                    </>
                  )}
                </p>
              </div>
            </div>
          </div>

          {/* If accepted but no link yet — offer a button to trigger conversion + payment */}
          {!redirectLink && (
            <button
              onClick={handleAccept}
              disabled={accepting}
              className="w-full bg-green-600 hover:bg-green-700 text-white font-bold py-4 rounded-xl transition shadow-lg disabled:opacity-50 disabled:cursor-not-allowed mb-4"
              data-testid="quote-accept-continue-payment"
            >
              {accepting ? (
                <span className="inline-flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" /> Préparation du paiement…
                </span>
              ) : (
                'Continuer vers le paiement →'
              )}
            </button>
          )}

          <div className="p-4 bg-slate-50 rounded-xl mb-4">
            <div className="text-sm text-slate-500">Récapitulatif</div>
            <div className="font-bold text-lg text-slate-900 mt-1">{quote.pack_name}</div>
            <div className="text-green-600 font-bold text-2xl mt-1">{quote.total_ttc?.toFixed(2)} € TTC/mois</div>
          </div>

          <p className="text-xs text-slate-400">
            Un email de confirmation vous a été envoyé. Pour toute question, contactez-nous à <a href="mailto:contact@phocearent.fr" className="text-slate-600 underline">contact@phocearent.fr</a>
          </p>
        </div>
      </div>
    );
  }

  // Check if expired
  const isExpired = quote.expiry_date && new Date(quote.expiry_date) < new Date();
  const sentAgo = quote.sent_at ? Math.floor((Date.now() - new Date(quote.sent_at).getTime()) / (1000 * 60 * 60 * 24)) : null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 py-8 px-4" data-testid="quote-accept-page">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <img src={LOGO_DARK} alt="Phocea Rent" className="h-12 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-slate-900">Votre devis</h1>
          <p className="text-slate-500 mt-1">Devis n° {quote.quote_number}</p>
          {sentAgo !== null && (
            <p className="text-sm text-slate-400 mt-1 flex items-center justify-center gap-1">
              <Clock className="w-3.5 h-3.5" />
              Envoyé {sentAgo === 0 ? "aujourd'hui" : `il y a ${sentAgo} jour${sentAgo > 1 ? 's' : ''}`}
            </p>
          )}
        </div>

        {/* Quote Card */}
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
          {/* Pack Info */}
          <div className="bg-slate-900 p-6 text-center">
            <div className="flex items-center justify-center gap-2 mb-2">
              <Package className="w-5 h-5 text-[#DFFF00]" />
              <h2 className="text-xl font-bold text-white">{quote.pack_name}</h2>
            </div>
            {quote.pack_surface && (
              <p className="text-slate-400 text-sm">{quote.pack_surface}</p>
            )}
            <div className="mt-3">
              <span className="text-4xl font-bold text-[#DFFF00]">{quote.total_ttc?.toFixed(2)} €</span>
              <span className="text-slate-400 text-sm ml-1">TTC / mois</span>
            </div>
          </div>

          {/* Details */}
          <div className="p-6 space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="flex items-start gap-3">
                <Euro className="w-4 h-4 text-slate-400 mt-0.5 shrink-0" />
                <div>
                  <div className="text-xs text-slate-500">Mensualité HT</div>
                  <div className="font-semibold text-slate-900">{quote.final_monthly_price?.toFixed(2)} €</div>
                  {quote.discount_amount > 0 && (
                    <div className="text-xs text-green-600">-{quote.discount_amount?.toFixed(2)} € de remise</div>
                  )}
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Euro className="w-4 h-4 text-slate-400 mt-0.5 shrink-0" />
                <div>
                  <div className="text-xs text-slate-500">TVA ({quote.tva_rate}%)</div>
                  <div className="font-semibold text-slate-900">{quote.tva_amount?.toFixed(2)} €</div>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Package className="w-4 h-4 text-slate-400 mt-0.5 shrink-0" />
                <div>
                  <div className="text-xs text-slate-500">Caution</div>
                  <div className="font-semibold text-slate-900">{quote.caution?.toFixed(2)} €</div>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Calendar className="w-4 h-4 text-slate-400 mt-0.5 shrink-0" />
                <div>
                  <div className="text-xs text-slate-500">Durée indicative</div>
                  <div className="font-semibold text-slate-900">{quote.duration_months} mois</div>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Clock className="w-4 h-4 text-slate-400 mt-0.5 shrink-0" />
                <div>
                  <div className="text-xs text-slate-500">Valide jusqu'au</div>
                  <div className={`font-semibold ${isExpired ? 'text-red-600' : 'text-slate-900'}`}>
                    {formatDate(quote.expiry_date)}
                    {isExpired && <span className="text-xs ml-1">(expiré)</span>}
                  </div>
                </div>
              </div>
            </div>

            {/* Frais de livraison + premier paiement clair */}
            {quote.transport_fees > 0 && (
              <div className="rounded-xl border border-amber-200 bg-amber-50 p-4">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5" />
                  <div className="text-sm">
                    <div className="font-semibold text-amber-900 mb-1">Frais de livraison ponctuels</div>
                    <p className="text-amber-800 leading-relaxed">
                      Les frais de livraison de <strong>{(quote.transport_total_ttc || quote.transport_fees * 1.2)?.toFixed(2)} € TTC</strong> sont facturés <strong>uniquement au premier paiement</strong>. Ils ne s'appliquent pas aux mensualités suivantes.
                    </p>
                  </div>
                </div>
              </div>
            )}

            <div className="rounded-xl bg-slate-900 text-white p-5">
              <div className="text-xs uppercase tracking-wide text-[#DFFF00] mb-3 font-semibold">Détail des paiements</div>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-slate-300">1<sup>er</sup> paiement (mensualité + livraison + caution)</span>
                  <span className="font-bold text-white">
                    {(((quote.first_payment_total_ttc) || quote.total_ttc) + (quote.caution || 0))?.toFixed(2)} €
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-300">Mensualités suivantes</span>
                  <span className="font-bold text-[#DFFF00]">{quote.total_ttc?.toFixed(2)} € / mois</span>
                </div>
              </div>
              <p className="text-xs text-slate-400 mt-3">
                La caution est restituée intégralement en fin de location si aucun dommage n'est constaté.
              </p>
            </div>

            {quote.payment_conditions && (
              <div className="pt-4 border-t border-slate-100">
                <div className="text-xs text-slate-500 mb-1">Conditions de paiement</div>
                <div className="text-sm text-slate-700">{quote.payment_conditions}</div>
              </div>
            )}

            <div className="pt-4 border-t border-slate-100">
              <p className="text-sm text-slate-500 text-center">
                <strong>Sans engagement</strong> — Location mensuelle résiliable à tout moment
              </p>
            </div>

            {/* Commercial */}
            {quote.commercial_name && (
              <div className="text-center text-sm text-slate-400">
                Votre interlocuteur : <strong className="text-slate-600">{quote.commercial_name}</strong>
              </div>
            )}
          </div>

          {/* Action */}
          <div className="p-6 bg-slate-50 border-t border-slate-100">
            {isExpired ? (
              <div className="text-center">
                <AlertTriangle className="w-8 h-8 text-amber-500 mx-auto mb-2" />
                <p className="text-slate-600 font-medium">Ce devis a expiré</p>
                <p className="text-sm text-slate-400 mt-1">Contactez votre commercial pour obtenir un nouveau devis.</p>
              </div>
            ) : error ? (
              <div className="text-center text-red-600 text-sm">{error}</div>
            ) : (
              <div className="text-center">
                <Button
                  onClick={handleAccept}
                  disabled={accepting}
                  data-testid="quote-accept-btn"
                  className="bg-green-600 hover:bg-green-700 text-white font-bold text-lg px-12 py-6 rounded-xl shadow-lg shadow-green-600/20"
                  style={{ fontSize: '16px', padding: '16px 48px' }}
                >
                  {accepting ? (
                    <><Loader2 className="w-5 h-5 animate-spin mr-2" /> Acceptation en cours...</>
                  ) : (
                    <><CheckCircle className="w-5 h-5 mr-2" /> Accepter et signer le devis</>
                  )}
                </Button>
                <p className="text-xs text-slate-400 mt-3">
                  En acceptant ce devis, vous confirmez votre accord sur les conditions proposées.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
